import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import ProfileTagsCollapse from '../../components/ProfileTagsCollapse/ProfileTagsCollapse';
import { ReactComponent as AddPhotoIcon } from '../../assets/profile-add-photo-icon.svg';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FRONT_DEFAULT_AVATAR, resolveAvatarUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';
import { resolveTheme, setTheme, subscribeToThemeChange } from '../../utils/theme';

function Profile() {
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [friendsCount, setFriendsCount] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [isDescOverflowing, setIsDescOverflowing] = useState(false);
  const descRef = useRef(null);

  const [visibleCount, setVisibleCount] = useState(5);
  const [remainingCount, setRemainingCount] = useState(0);
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => resolveTheme() === 'dark');

  const [lightbox, setLightbox] = useState({
    isOpen: false,
    images: [],
    currentIndex: 0
  });

  const openLightbox = (images, index = 0) => {
    setLightbox({
      isOpen: true,
      images: images || [],
      currentIndex: index
    });
  };

  const closeLightbox = () => {
    setLightbox({
      isOpen: false,
      images: [],
      currentIndex: 0
    });
  };

  const nextLightboxImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex + 1) % prev.images.length
    }));
  };

  const prevLightboxImage = () => {
    setLightbox(prev => ({
      ...prev,
      currentIndex: (prev.currentIndex - 1 + prev.images.length) % prev.images.length
    }));
  };

  const toggleTheme = () => {
    const nextTheme = isDarkMode ? 'light' : 'dark';
    setTheme(nextTheme);
    setIsDarkMode(nextTheme === 'dark');
  };
  const description = userData?.description || userData?.bio || "";

  useEffect(() => {
    setIsDarkMode(resolveTheme() === 'dark');

    return subscribeToThemeChange((theme) => {
      setIsDarkMode(theme === 'dark');
    });
  }, []);

  useEffect(() => {
    let isActive = true;

    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/log_in');
        return;
      }

      try {
        const response = await fetch(`${API_BASE}/api/Profile/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/log_in');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }

        const data = await response.json();
        if (!isActive) return;
        setUserData(data);

        try {
          const likesResp = await fetch(`${API_BASE}/api/Likes/mutual-likes`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!isActive) return;
          if (likesResp.ok) {
            const likes = await likesResp.json();
            if (isActive) {
              setFriendsCount(Array.isArray(likes) ? likes.length : data.friendsCount ?? 0);
            }
          } else if (isActive) {
            setFriendsCount(data.friendsCount ?? 0);
          }
        } catch (e) {
          console.warn('Failed to fetch mutual likes', e);
          if (isActive) {
            setFriendsCount(data.friendsCount ?? 0);
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchProfile();
    return () => {
      isActive = false;
    };
  }, [navigate]);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/log_in');
  };

  const compressImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob((blob) => {
          const webpName = file.name.replace(/\.[^/.]+$/, '') + '.webp';
          resolve(new File([blob], webpName, { type: 'image/webp', lastModified: Date.now() }));
        }, 'image/webp', 0.85);

        URL.revokeObjectURL(img.src);
      };
      img.onerror = (error) => {
        URL.revokeObjectURL(img.src);
        reject(error);
      };
    });
  };

  const handleAvatarUploadSelect = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarUploadChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нужна авторизация');
      return;
    }

    const fd = new FormData();

    try {
      setIsUploadingAvatar(true);

      const compressedFiles = await Promise.all(
        Array.from(files).map(async (file) => {
          if (file.type.startsWith('image/')) {
            try {
              return await compressImage(file, 1920, 1920);
            } catch (error) {
              console.error('Failed to compress image:', error);
              return file;
            }
          }
          return file;
        })
      );

      compressedFiles.forEach((file) => fd.append('files', file));

      const resp = await fetch(`${API_BASE}/api/Profile/avatars?makePrimary=true`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });

      if (!resp.ok) {
        const errorText = await resp.text();
        alert('Ошибка при загрузке фото: ' + (errorText || resp.statusText));
        return;
      }

      const data = await resp.json();

      setUserData((prev) => {
        if (!prev) return prev;
        const uploadedPhotos = Array.isArray(data.uploaded) ? data.uploaded : [];
        const previousPhotos = Array.isArray(prev.photos) ? prev.photos : [];

        return {
          ...prev,
          avatarUrl: data.avatarUrl || prev.avatarUrl,
          photos: [...uploadedPhotos, ...previousPhotos]
        };
      });

      if (data.avatarUrl) {
        const userObj = JSON.parse(localStorage.getItem('user') || '{}');
        userObj.avatarUrl = data.avatarUrl;
        localStorage.setItem('user', JSON.stringify(userObj));
      }
    } catch (error) {
      console.error(error);
      alert('Ошибка сети при загрузке фото');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setIsUploadingAvatar(false);
    }
  };

  const allPhotos = (userData?.photos || []).map(p => {
    if (typeof p === 'string') return resolveAvatarUrl(p);
    return resolveAvatarUrl(p.url || p.Url);
  });
  
  const displayPhotos = (() => {
    return allPhotos.map((url, idx) => ({ id: idx, url, isPlaceholder: false }));
  })();

  // Dynamically measure how many items fit inside a single row of the photos container
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      if (isExpanded) {
        setVisibleCount(displayPhotos.length);
        setRemainingCount(0);
        return;
      }

      // Temporarily set flex-wrap to nowrap and display all elements to measure natural widths
      container.style.flexWrap = 'nowrap';
      
      const children = Array.from(container.children);
      const hadHiddenClass = children.map(c => c.classList.contains('profile-photos__item--hidden'));

      children.forEach(c => {
        c.classList.remove('profile-photos__item--hidden');
        c.style.display = 'flex';
      });

      const widths = children.map(child => child.getBoundingClientRect().width);

      // Restore direct DOM override so React's virtual DOM styles take full control!
      children.forEach((c, i) => {
        c.style.removeProperty('display');
        if (hadHiddenClass[i]) {
          c.classList.add('profile-photos__item--hidden');
        }
      });
      container.style.removeProperty('flex-wrap');

      const containerWidth = container.offsetWidth;
      const gap = parseFloat(getComputedStyle(container).gap) || 10;

      let totalWidth = 0;
      let fitCount = 0;

      for (let i = 0; i < widths.length; i++) {
        const itemWidth = widths[i] || (containerWidth * 140 / 1920);
        const nextWidth = totalWidth + itemWidth + (i > 0 ? gap : 0);
        if (nextWidth <= containerWidth) {
          totalWidth = nextWidth;
          fitCount++;
        } else {
          break;
        }
      }

      // Safe check: show at least 1 photo if some exist
      if (fitCount === 0 && widths.length > 0) {
        fitCount = 1;
      }

      if (fitCount < displayPhotos.length) {
        setVisibleCount(fitCount);
        setRemainingCount(displayPhotos.length - fitCount + 1);
      } else {
        setVisibleCount(displayPhotos.length);
        setRemainingCount(0);
      }
    };

    // Run measurement inside requestAnimationFrame to ensure DOM is ready
    const handle = requestAnimationFrame(measure);

    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(handle);
      window.removeEventListener('resize', measure);
    };
  }, [userData, isExpanded, displayPhotos]);

  if (loading) {
    return (
      <div className="profile-page profile-page--loading">
        <div>Загрузка...</div>
      </div>
    );
  }

  const firstName = userData?.firstName || "";
  const lastName = userData?.lastName || "";
  const birthDate = userData?.birthDate ? new Date(userData.birthDate).toLocaleDateString('ru-RU') : "";
  const username = userData?.username || userData?.nickname || "username";
  const avatarUrl = userData?.avatarUrl ? resolveAvatarUrl(userData.avatarUrl) : FRONT_DEFAULT_AVATAR;

  const rawInterests = (userData?.onboarding && userData.onboarding.length > 0)
      ? userData.onboarding.flatMap(q => q.selected?.map(s => s.text) || [])
      : [];

  const interests = rawInterests.filter(interest => {
    if (!interest) return false;
    const lower = interest.toLowerCase().trim();
    if (lower === 'другое' || lower === 'other') return false;
    if (lower.startsWith('option #')) return false;
    return true;
  });

  return (
    <div className="profile-page">
      
      <div className="profile-page__canvas">
<div className="profile-layout">
          {/* Left Column Card */}
          <div className="profile-card">
            <div className="profile-card__avatar-wrap" onClick={() => openLightbox([avatarUrl], 0)} style={{cursor: 'pointer'}}>
              <img 
                src={avatarUrl} 
                alt="Avatar" 
                className="profile-card__avatar"
                onError={withAvatarFallback()} 
              />
            </div>

            {/* Mobile Header Block */}
            <div className="profile-card__name-block-mobile">
              <h2 className="profile-card__name-mobile">{firstName} {lastName}</h2>
              <div className="profile-card__nickname-mobile">
                {username.startsWith('@') ? username : `@${username}`}
              </div>
            </div>

            {/* Edit / Switch row */}
            <div className="profile-card__edit-row">
              <button
                className={`profile-card__switch--mobile profile-card__switch--mobile-inline ${isDarkMode ? 'is-on' : ''}`}
                onClick={toggleTheme}
                title="РџРµСЂРµРєР»СЋС‡РёС‚СЊ С‚РµРјСѓ"
              >
                <span></span>
              </button>
              <button 
                className={`profile-card__switch ${isDarkMode ? 'is-on' : ''}`} 
                onClick={toggleTheme}
                title="Переключить тему"
              >
                <span></span>
              </button>
              <Link to="/edit-profile" className="profile-card__edit">
                <svg width="14" height="14" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.4588 0.20197C17.5256 0.137948 17.6049 0.0871543 17.6922 0.0524969C17.7795 0.0178396 17.8731 0 17.9677 0C18.0622 0 18.1558 0.0178396 18.2431 0.0524969C18.3305 0.0871543 18.4098 0.137948 18.4765 0.20197L22.7888 4.32679C22.8558 4.39065 22.9089 4.46651 22.9451 4.55003C22.9813 4.63355 23 4.72309 23 4.81352C23 4.90394 22.9813 4.99348 22.9451 5.077C22.9089 5.16052 22.8558 5.23639 22.7888 5.30025L8.41448 19.0496C8.3455 19.1152 8.26335 19.1666 8.17299 19.2009L0.985803 21.9508C0.855192 22.0008 0.712109 22.013 0.574293 21.986C0.436477 21.9589 0.309988 21.8938 0.210508 21.7986C0.111028 21.7035 0.0429317 21.5825 0.0146609 21.4507C-0.0136098 21.3189 -0.000811566 21.182 0.051469 21.0571L2.92634 14.1824C2.96218 14.0959 3.01597 14.0173 3.08446 13.9514L17.4588 0.20197ZM16.1091 3.43858L19.4051 6.59132L21.2637 4.81352L17.9677 1.66078L16.1091 3.43858ZM18.3889 7.5634L15.0928 4.41066L5.74947 13.3478V13.7506H6.46819C6.6588 13.7506 6.84161 13.8231 6.9764 13.952C7.11118 14.0809 7.18691 14.2558 7.18691 14.4381V15.1256H7.90562C8.09624 15.1256 8.27905 15.198 8.41384 15.3269C8.54862 15.4558 8.62434 15.6307 8.62434 15.813V16.5005H9.04551L18.3889 7.5634ZM4.35803 14.6787L4.20566 14.8245L2.00926 20.0781L7.50171 17.9772L7.65407 17.8314C7.51697 17.7824 7.39877 17.6946 7.31529 17.5796C7.23181 17.4646 7.18702 17.328 7.18691 17.188V16.5005H6.46819C6.27757 16.5005 6.09476 16.4281 5.95998 16.2992C5.82519 16.1702 5.74947 15.9954 5.74947 15.813V15.1256H5.03075C4.88439 15.1255 4.74155 15.0826 4.62134 15.0028C4.50112 14.9229 4.40926 14.8099 4.35803 14.6787Z" fill="#FF6B35"/>
                </svg>
                <span>Редактировать профиль</span>
              </Link>
            </div>

            {/* Meta Data Block */}
            <div className="profile-card__meta">
              {userData?.email && (
                <div>
                  <dt>Почта</dt>
                  <dd title={userData.email}>{userData.email}</dd>
                </div>
              )}
              <div>
                <dt>Дата рождения</dt>
                <dd>{birthDate || '—'}</dd>
              </div>
              <div>
                <dt>Профессия</dt>
                <dd>{userData?.jobTitle || userData?.profession || '—'}</dd>
              </div>
              <div>
                <dt>Город</dt>
                <dd>{userData?.city || '—'}</dd>
              </div>
            </div>

            {/* Logout button */}
            <button onClick={handleLogout} className="profile-card__logout">
              <i className="bi bi-box-arrow-right"></i>
              <span>Выйти из профиля</span>
            </button>

          </div>

          {/* Right Column Details */}
          <div className="profile-details">
            <div className="profile-details__header">
              <h1>{firstName} {lastName}</h1>
              <p>
                {username.startsWith('@') ? username : `@${username}`}
              </p>
            </div>

            <div className="profile-details__section">
              <h2>Описание</h2>
              <div 
                className={`profile-details__about ${isDescOverflowing ? 'is-overflowing' : ''} ${isDescExpanded ? 'is-expanded' : ''}`}
                onClick={() => {
                  setIsDescExpanded(!isDescExpanded);
                }}
                style={{ cursor: isDescOverflowing ? 'pointer' : 'default', position: 'relative' }}
              >
                <p 
                  ref={descRef}
                  className={`profile-details__about-text ${description ? '' : 'is-ghost'}`}
                >
                  {description || "Описание отсутствует"}
                </p>
              </div>
            </div>

            {interests.length > 0 && (
              <ProfileTagsCollapse tags={interests} className="profile-tags" />
            )}

            <div className="profile-photos-section">
              <h2>Фотографии</h2>
              {displayPhotos.length === 0 ? (
                <button
                  type="button"
                  className="profile-photos__add-button"
                  onClick={handleAvatarUploadSelect}
                  disabled={isUploadingAvatar}
                >
                  <AddPhotoIcon className="profile-photos__add-button-icon" aria-hidden="true" />
                  <span>Добавить фото</span>
                </button>
              ) : (
              <div className={`profile-photos edit-profile-photos ${isExpanded ? 'edit-profile-photos--expanded' : ''}`} ref={containerRef}>
                {displayPhotos.map((photo, idx) => {
                  const isHidden = !isExpanded && idx >= visibleCount;
                  const isMore = !isExpanded && idx === visibleCount - 1 && remainingCount > 0;
                  
                  return (
                    <div 
                      key={photo.id || `profile-ph-${idx}`} 
                      className={`profile-photos__item ${photo.isPlaceholder ? 'profile-photos__item--empty' : ''} ${isMore ? 'profile-photos__item--more' : ''} ${isHidden ? 'profile-photos__item--hidden' : ''}`}
                      onClick={() => {
                        if (isMore) {
                          setIsExpanded(true);
                        } else if (photo.url) {
                          openLightbox(allPhotos, idx);
                        }
                      }}
                      style={{
                        cursor: (isMore || photo.url) ? 'pointer' : 'default',
                        position: 'relative'
                      }}
                    >
                      {photo.url ? (
                        <img src={photo.url} alt="" />
                      ) : (
                        <div className="profile-photos__empty" style={{background: '#ffffff', width: '100%', height: '100%'}}></div>
                      )}
                      
                      {isMore && (
                        <>
                          <div className="profile-photos__film"></div>
                          <div className="profile-photos__more">+{remainingCount}</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showLogoutModal && (
        <div className="profile-logout-modal">
          <div className="profile-logout-modal__backdrop" onClick={() => setShowLogoutModal(false)}></div>
          <div className="profile-logout-modal__content">
            <h2>Действительно хочешь выйти<br/>из профиля?</h2>
            <div className="profile-logout-modal__actions">
              <button onClick={confirmLogout}>Да</button>
              <button onClick={() => setShowLogoutModal(false)}>Нет, остаться</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Pop-up Viewer */}
      {lightbox.isOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <i className="bi bi-x"></i>
          </button>
          {lightbox.images.length > 1 && (
            <>
              <button 
                className="lightbox-arrow arrow-left" 
                onClick={(e) => { e.stopPropagation(); prevLightboxImage(); }}
              >
                <i className="bi bi-chevron-left"></i>
              </button>
              <button 
                className="lightbox-arrow arrow-right" 
                onClick={(e) => { e.stopPropagation(); nextLightboxImage(); }}
              >
                <i className="bi bi-chevron-right"></i>
              </button>
            </>
          )}
          <img 
            src={lightbox.images[lightbox.currentIndex]} 
            alt="fullscreen preview" 
            className="lightbox-image" 
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden' }}
        accept="image/*"
        onChange={handleAvatarUploadChange}
      />

      <Footer />
    </div>
  );
}

export default Profile;
